import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Menu, X, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../components/ui/sheet";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Diensten' },
    { path: '/pricing', label: 'Prijzen' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/contact', label: 'Contact' },
  ];

  // Special external link
  const portalLink = { path: 'https://hub.cylostream.com', label: 'Klantenportaal', isExternal: true };

  return (
    <header className="bg-background/95 backdrop-blur border-b sticky top-0 z-40 shadow-sm">
      <div className="container mx-auto h-16 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold px-4">
          Cylostream<span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Web</span>
        </Link>
        <div className="flex items-center px-4">
          <nav className="hidden md:block">
            <ul className="flex space-x-6 items-center">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink 
                    to={item.path} 
                    className={({ isActive }) => 
                      `hover:text-primary transition-colors ${isActive ? 'font-semibold text-primary' : 'text-foreground'}`
                    }
                    end={item.path === '/'}
                  >
                    <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
                      {item.label}
                    </span>
                  </NavLink>
                </li>
              ))}
              <li>
                <a href="/original/" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary border border-border rounded-md px-2 py-1 transition-colors hover:border-primary">
                  AI Diensten
                </a>
              </li>
              <li className="ml-2">
                <a 
                  href={portalLink.path}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center bg-primary hover:bg-primary/90 text-white rounded-md px-3 py-1 transition-colors text-sm"
                >
                  {portalLink.label}
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </li>
            </ul>
          </nav>
          <div className="md:hidden ml-4">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  aria-label="Open main menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-background p-6 flex flex-col z-50">
                <div className="flex justify-between items-center mb-8">
                  <Link to="/" className="text-xl font-bold">
                    Cylostream<span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Web</span>
                  </Link>
                </div>
                <nav className="flex-grow">
                  <ul className="flex flex-col space-y-6 text-left text-lg">
                    {navItems.map((item) => (
                      <li key={item.path}>
                        <SheetClose asChild>
                          <NavLink 
                            to={item.path} 
                            className={({ isActive }) => 
                              `block py-2 hover:text-primary transition-colors ${isActive ? 'font-bold text-primary' : 'text-foreground'}`
                            }
                            end={item.path === '/'}
                          >
                            <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
                              {item.label}
                            </span>
                          </NavLink>
                        </SheetClose>
                      </li>
                    ))}
                    <li>
                      <SheetClose asChild>
                        <a 
                          href="/original/" 
                          rel="noopener noreferrer" 
                          className="block py-2 text-muted-foreground hover:text-primary transition-colors"
                        >
                          AI Diensten
                        </a>
                      </SheetClose>
                    </li>
                    <li>
                      <SheetClose asChild>
                        <a 
                          href={portalLink.path}
                          target="_blank"
                          rel="noopener noreferrer" 
                          className="flex items-center py-2 text-primary font-semibold hover:text-primary/80 transition-colors"
                        >
                          {portalLink.label}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </SheetClose>
                    </li>
                  </ul>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
